import React, { useState, useEffect, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import { rgba, p3, media } from "../styles/stylesTheme";
// stylesUtils import removed - not currently needed

const Tooltip = forwardRef(
    (
        {
            content,
            position,
            show,
            onClick,
            showLoader,
            forceHide = false,
        },
        ref
    ) => {
        const [adjustedPosition, setAdjustedPosition] = useState(
            position || { top: 0, left: 0 }
        );
        const [loaderPosition, setLoaderPosition] = useState(
            position || { top: 0, left: 0 }
        );
        const [isScrolling, setIsScrolling] = useState(false);

        useEffect(() => {
            const safePosition = position || { top: 0, left: 0 };
            setAdjustedPosition(safePosition);
            setLoaderPosition(safePosition);
        }, [position]);

        useEffect(() => {
            if (!show && !showLoader) return;

            const updateTooltipPosition = () => {
                if (!show || forceHide) return;

                const highlightedSpans =
                    document.querySelectorAll("span.highlighted");

                if (highlightedSpans.length === 0) {
                    return;
                }

                let minLeft = Infinity;
                let maxRight = -Infinity;

                highlightedSpans.forEach((span) => {
                    const rect = span.getBoundingClientRect();
                    minLeft = Math.min(minLeft, rect.left);
                    maxRight = Math.max(maxRight, rect.right);
                });

                const midXPos = (minLeft + maxRight) / 2;
                const firstSpan = highlightedSpans[0];
                const firstRect = firstSpan.getBoundingClientRect();
                const topPos = firstRect.top;

                setAdjustedPosition({
                    top: topPos - 45,
                    left: midXPos,
                });
            };

            const updateLoaderPosition = () => {
                if (!showLoader || forceHide) return;

                const highlightedSpans =
                    document.querySelectorAll("span.highlighted");

                if (highlightedSpans.length === 0) {
                    return;
                }

                let minLeft = Infinity;
                let maxRight = -Infinity;

                highlightedSpans.forEach((span) => {
                    const rect = span.getBoundingClientRect();
                    minLeft = Math.min(minLeft, rect.left);
                    maxRight = Math.max(maxRight, rect.right);
                });

                const midXPos = (minLeft + maxRight) / 2;
                const firstSpan = highlightedSpans[0];
                const firstRect = firstSpan.getBoundingClientRect();
                const topPos = firstRect.top;

                setLoaderPosition({
                    top: topPos - 45,
                    left: midXPos,
                });
            };

            let scrollTimeout;
            const handleScroll = () => {
                setIsScrolling(true);
                updateTooltipPosition();
                updateLoaderPosition();

                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    setIsScrolling(false);
                }, 150);
            };

            const resizeHandler = () => {
                updateTooltipPosition();
                updateLoaderPosition();
            };

            document.addEventListener("scroll", handleScroll, true);
            window.addEventListener("resize", resizeHandler);

            updateTooltipPosition();
            updateLoaderPosition();

            return () => {
                document.removeEventListener("scroll", handleScroll, true);
                window.removeEventListener("resize", resizeHandler);
                clearTimeout(scrollTimeout);
            };
        }, [show, showLoader, forceHide]);

        if (forceHide) {
            return <div style={{ display: "none" }} />;
        }

        return (
            <>
                {/* Word Lookup Loader */}
                <AnimatePresence>
                    {showLoader && !forceHide && (
                        <StyledWordLookupLoader
                            initial={{
                                opacity: 0,
                                y: "30%",
                                x: "-50%",
                            }}
                            animate={{
                                opacity: 1,
                                y: "10%",
                                x: "-50%",
                            }}
                            exit={{
                                opacity: 0,
                                y: "-10%",
                                x: "-50%",
                                scale: 0.95,
                                transition: {
                                    duration: 0.2,
                                    ease: "easeOut",
                                },
                            }}
                            transition={{
                                y: {
                                    type: "spring",
                                    bounce: 1,
                                    stiffness: 300,
                                    damping: 16,
                                    duration: 0.3,
                                },
                                opacity: {
                                    duration: 0.15,
                                },
                            }}
                            style={{
                                top: loaderPosition.top,
                                left: loaderPosition.left,
                                transform: isScrolling
                                    ? "translateX(-50%) translateY(-100%)"
                                    : undefined,
                                opacity: isScrolling ? 1 : undefined,
                            }}
                        >
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                        </StyledWordLookupLoader>
                    )}
                </AnimatePresence>

                {/* Main Tooltip */}
                <AnimatePresence>
                    {show && !forceHide && (
                        <StyledTooltip
                            ref={ref}
                            onClick={onClick}
                            initial={
                                !isScrolling
                                    ? {
                                          opacity: 0,
                                          y: "-80%",
                                          x: "-50%",
                                      }
                                    : false
                            }
                            animate={
                                !isScrolling
                                    ? {
                                          opacity: 1,
                                          y: "-100%",
                                          x: "-50%",
                                      }
                                    : false
                            }
                            exit={{
                                opacity: 0,
                                transition: {
                                    duration: 0.3,
                                    ease: "easeOut",
                                },
                            }}
                            transition={
                                !isScrolling
                                    ? {
                                          y: {
                                              type: "spring",
                                              bounce: 0.15,
                                              stiffness: 400,
                                              damping: 22,
                                              duration: 0.35,
                                          },
                                          opacity: {
                                              duration: 0.25,
                                              ease: "easeOut",
                                          },
                                      }
                                    : false
                            }
                            style={{
                                top: adjustedPosition.top,
                                left: adjustedPosition.left,
                                transform: isScrolling
                                    ? "translateX(-50%) translateY(-100%)"
                                    : undefined,
                                opacity: isScrolling ? 1 : undefined,
                            }}
                        >
                            <div>{content}</div>
                        </StyledTooltip>
                    )}
                </AnimatePresence>
            </>
        );
    }
);

export default Tooltip;

const StyledTooltip = styled(motion.div)`
    position: fixed;
    border-radius: 1em;
    background: ${({ theme }) => rgba(theme, "colorTranslate", 0.3)};
    background: ${({ theme }) => p3(theme, "pcolorTranslate", 0.3)};
    backdrop-filter: blur(1em);
    -webkit-backdrop-filter: blur(1em);
    box-shadow: 0px 1px 0.5em 0px rgba(43, 43, 46, 0.2);
    color: #fff;
    z-index: 10;
    font-size: 1.1em;
    cursor: pointer;
    padding: 0.2em;
    margin-top: 1.8em;
    z-index: 10000000;

    > div {
        border-radius: 0.8em;
        background: ${({ theme }) => rgba(theme, "colorTranslate", 1)};
        background: ${({ theme }) => p3(theme, "pcolorTranslate", 1)};
        padding: 0.8em 2.5em 0.75em 1.1em;
        color: rgba(35, 34, 18, 0.9);
        text-align: center;
        line-height: 1.5em;
        max-width: 22em;

        .tooltip-text {
            padding-right: 0.8em;
            font-family: var(--med);
        }

        .tooltip-btn {
            width: 1.8em;
            height: 1.8em;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            border: none;
            background: rgba(35, 34, 18, 0.1);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 0;
            font-size: 0.9em;
            font-family: var(--med);
            color: rgba(35, 34, 18, 0.7);
            box-shadow: inset 0 0 0 0.15em rgba(35, 34, 18, 0.7);

            &:hover {
                background: rgba(35, 34, 18, 0.15);
                opacity: 1;
            }

            &:active {
                transform: translateY(-50%) scale(0.95);
            }

            img {
                width: 0.9em;
                height: 0.9em;
                position: relative;
                top: auto;
                right: auto;
                transform: none;
            }
        }

        .tooltip-btn-add {
            right: 1.1em;
        }

        .tooltip-btn-glossary {
            right: 1.1em;
        }

        /* When add button exists, adjust padding and move search icon left */
        &:has(.tooltip-btn-add) {
            padding-right: 4.5em;

            .tooltip-btn-glossary {
                right: 3.2em;
            }
        }

        .tooltip-search {
            img,
            svg {
                top: -0.05em;
                left: -0.05em;
            }
        }

        &::after {
            content: "";
            position: absolute;
            bottom: -1.2em;
            left: 50%;
            transform: translateX(-50%);
            border-width: 0.8em;
            border-style: solid;
            border-color: ${({ theme }) => rgba(theme, "colorTranslate", 1)}
                transparent transparent transparent;
            border-color: ${({ theme }) => p3(theme, "pcolorTranslate", 1)}
                transparent transparent transparent;
        }
    }

    &:hover {
        > div {
            .tooltip-btn {
                opacity: 0.8;
            }
        }
    }

    ${media.mobile} {
        margin-top: 2.4em;
        font-size: 1.3em;
        left: 50% !important;

        > div {
            &::after {
                display: none !important;
            }
        }
    }
`;

const StyledWordLookupLoader = styled(motion.div)`
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3em;
    z-index: 10000000;
    background: ${({ theme }) => rgba(theme, "colorTranslate", 0.5)};
    background: ${({ theme }) => p3(theme, "pcolorTranslate", 0.5)};
    backdrop-filter: blur(1em);
    -webkit-backdrop-filter: blur(1em);
    padding: 1em 0.8em;
    border-radius: 0.8em;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    font-size: 0.8em;

    .dot {
        width: 3px;
        height: 3px;
        border-radius: 50%;
        animation: bounce 1.4s ease-in-out infinite both;
        opacity: 0.6;
        background: rgba(0, 0, 0, 0.5);

        &:nth-child(1) {
            animation-delay: -0.32s;
        }

        &:nth-child(2) {
            animation-delay: -0.16s;
        }

        &:nth-child(3) {
            animation-delay: 0s;
        }
    }

    @keyframes bounce {
        0%,
        20%,
        50%,
        80%,
        100% {
            transform: translateY(0);
        }
        40% {
            transform: translateY(-0.2em);
        }
        60% {
            transform: translateY(0.2em);
        }
    }

    ${media.mobile} {
        margin-top: 1.8em;
        left: 50% !important;
    }
`;
